import React, { useEffect, useMemo, useState, Children } from 'react';
import { dashboardSeed } from '../lib/data';
import { ActivityIcon, TrendingUpIcon, PercentIcon, DollarSignIcon, ClockIcon, BarChart3Icon, CheckCircleIcon, ZapIcon, FileTextIcon } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
export function Dashboard() {
  const [data, setData] = useState(dashboardSeed);
  const [now, setNow] = useState(new Date());
  // Set page title
  useEffect(() => {
    document.title = 'Mint | Dashboard';
  }, []);
  // clock
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(t);
  }, []);
  // gentle live ticks (no blinking)
  useEffect(() => {
    const t = setInterval(() => {
      setData(prev => ({
        ...prev,
        agents: prev.agents.map((a, i) => {
          // small bounded jitter per agent
          const jitter = i === 0 ? 2 : 1;
          const ips = Math.max(1, a.ips + Math.round((Math.random() - 0.5) * 2 * jitter));
          const processed = a.processed + Math.max(1, Math.round(ips * 0.6));
          return {
            ...a,
            ips,
            processed
          };
        })
      }));
    }, 1500);
    return () => clearInterval(t);
  }, []);
  const hhmmss = useMemo(() => {
    // Add 3 hours to get East Coast time
    const estTime = new Date(now.getTime() + 3 * 60 * 60 * 1000);
    return estTime.toLocaleTimeString('en-US', {
      hour12: false,
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  }, [now]);
  // helpers
  const fmtInt = (n: number) => n.toLocaleString();
  const fmtUSD = (n: number) => `$${n.toLocaleString(undefined, {
    maximumFractionDigits: 1
  })}`;
  // Generate sparkline data
  const generateSparklineData = (points: number = 20) => {
    const data = [];
    for (let i = 0; i < points; i++) {
      data.push(20 + Math.random() * 10 - i * 0.2);
    }
    return data;
  };
  const sparklineData = useMemo(() => generateSparklineData(), []);
  const Sparkline = ({
    data
  }: {
    data: number[];
  }) => {
    const max = Math.max(...data);
    const min = Math.min(...data);
    const range = max - min;
    const points = data.map((value, index) => {
      const x = index / (data.length - 1) * 100;
      const y = 30 - (value - min) / range * 28;
      return `${x},${y}`;
    }).join(' ');
    return <svg className="w-full h-8 mt-2" viewBox="0 0 100 30" data-id="element-1205">
        <polyline points={points} fill="none" stroke="#6b7280" strokeWidth="1.5" data-id="element-1206" />
      </svg>;
  };
  return <div className="min-h-screen bg-gray-50 px-6 py-6" data-id="element-1207">
      {/* Header */}
      <div className="flex items-center justify-between mb-8" data-id="element-1208">
        <h1 className="text-3xl font-semibold text-gray-900" data-id="element-1209">Dashboard</h1>
        <div className="flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2 shadow-sm" data-id="element-1210">
          <div className="relative" data-id="element-1211">
            <span className="inline-block h-2 w-2 rounded-full bg-emerald-500" data-id="element-1212" />
            <span className="absolute inset-0 h-2 w-2 rounded-full bg-emerald-500 animate-ping" data-id="element-1213" />
          </div>
          <span className="text-sm text-gray-700" data-id="element-1214">Live, {hhmmss} EST</span>
        </div>
      </div>
      {/* Validation Funnel */}
      <div className="mb-6" data-id="element-1215">
        <h2 className="text-lg font-medium text-gray-900 mb-3" data-id="element-1216">
          Validation Funnel
        </h2>
        <div className="grid grid-cols-4 gap-4" data-id="element-1217">
          <Card data-id="element-1218">
            <div className="pb-2 pt-4 px-4" data-id="element-1219">
              <div className="text-xs text-gray-500 uppercase" data-id="element-1220">
                Invoices Processed
              </div>
            </div>
            <CardContent className="pt-0" data-id="element-1221">
              <div className="flex items-baseline gap-2" data-id="element-1222">
                <div className="text-3xl font-bold" data-id="element-1223">498</div>
                <span className="text-xs text-green-600 font-medium" data-id="element-1224">
                  ↑ 2.3%
                </span>
              </div>
              <div className="text-xs text-gray-500" data-id="element-1225">Past 30 Days</div>
            </CardContent>
          </Card>
          <Card data-id="element-1226">
            <div className="pb-2 pt-4 px-4" data-id="element-1227">
              <div className="text-xs text-gray-500 uppercase" data-id="element-1228">
                Pipeline (Active)
              </div>
            </div>
            <CardContent className="pt-0" data-id="element-1229">
              <div className="flex items-baseline gap-2" data-id="element-1230">
                <div className="text-3xl font-bold" data-id="element-1231">847,293</div>
                <span className="text-xs text-gray-600 font-medium" data-id="element-1232">
                  → 0.1%
                </span>
              </div>
              <div className="text-xs text-gray-500" data-id="element-1233">Receivables to Verify</div>
            </CardContent>
          </Card>
          <Card data-id="element-1234">
            <div className="pb-2 pt-4 px-4" data-id="element-1235">
              <div className="text-xs text-gray-500 uppercase" data-id="element-1236">
                Active Queue
              </div>
            </div>
            <CardContent className="pt-0" data-id="element-1237">
              <div className="flex items-baseline gap-2" data-id="element-1238">
                <div className="text-3xl font-bold" data-id="element-1239">8,473</div>
                <span className="text-xs text-green-600 font-medium" data-id="element-1240">
                  ↑ 5.2%
                </span>
              </div>
              <div className="text-xs text-gray-500" data-id="element-1241">In Processing</div>
            </CardContent>
          </Card>
          <Card data-id="element-1242">
            <div className="pb-2 pt-4 px-4" data-id="element-1243">
              <div className="text-xs text-gray-500 uppercase" data-id="element-1244">Accuracy</div>
            </div>
            <CardContent className="pt-0" data-id="element-1245">
              <div className="flex items-baseline gap-2" data-id="element-1246">
                <div className="text-3xl font-bold" data-id="element-1247">99.7%</div>
                <span className="text-xs text-green-600 font-medium" data-id="element-1248">
                  ↑ 0.2%
                </span>
              </div>
              <div className="text-xs text-gray-500" data-id="element-1249">≥ 0.85 Confidence</div>
            </CardContent>
          </Card>
        </div>
      </div>
      {/* Engine Performance */}
      <div className="mb-6" data-id="element-1250">
        <h2 className="text-lg font-medium text-gray-900 mb-3" data-id="element-1251">
          Engine Performance
        </h2>
        <div className="grid grid-cols-4 gap-4" data-id="element-1252">
          <Card data-id="element-1253">
            <div className="pb-2 pt-4 px-4" data-id="element-1254">
              <div className="text-xs text-gray-500 uppercase" data-id="element-1255">
                Processing Volume
              </div>
            </div>
            <CardContent className="pt-0" data-id="element-1256">
              <div className="text-3xl font-bold" data-id="element-1257">$63.4M</div>
              <Sparkline data={sparklineData} data-id="element-1258" />
            </CardContent>
          </Card>
          <Card data-id="element-1259">
            <div className="pb-2 pt-4 px-4" data-id="element-1260">
              <div className="text-xs text-gray-500 uppercase" data-id="element-1261">Throughput</div>
            </div>
            <CardContent className="pt-0" data-id="element-1262">
              <div className="flex items-baseline gap-2" data-id="element-1263">
                <div className="text-3xl font-bold" data-id="element-1264">855 TPS</div>
                <span className="text-xs text-green-600 font-medium" data-id="element-1265">
                  ↑ 1.8%
                </span>
              </div>
              <div className="text-xs text-gray-500" data-id="element-1266">Transactions/Second</div>
            </CardContent>
          </Card>
          <Card data-id="element-1267">
            <div className="pb-2 pt-4 px-4" data-id="element-1268">
              <div className="text-xs text-gray-500 uppercase" data-id="element-1269">
                Processing Time
              </div>
            </div>
            <CardContent className="pt-0" data-id="element-1270">
              <div className="flex items-baseline gap-2" data-id="element-1271">
                <div className="text-3xl font-bold" data-id="element-1272">36s</div>
                <span className="text-xs text-green-600 font-medium" data-id="element-1273">
                  ↓ 3.1%
                </span>
              </div>
              <div className="text-xs text-gray-500" data-id="element-1274">Average Latency</div>
            </CardContent>
          </Card>
          <Card data-id="element-1275">
            <div className="pb-2 pt-4 px-4" data-id="element-1276">
              <div className="text-xs text-gray-500 uppercase" data-id="element-1277">
                Success Rate
              </div>
            </div>
            <CardContent className="pt-0" data-id="element-1278">
              <div className="flex items-baseline gap-2" data-id="element-1279">
                <div className="text-3xl font-bold" data-id="element-1280">99.7%</div>
                <span className="text-xs text-gray-600 font-medium" data-id="element-1281">
                  → 0.0%
                </span>
              </div>
              <div className="text-xs text-gray-500" data-id="element-1282">≥ 0.85 Confidence</div>
            </CardContent>
          </Card>
        </div>
      </div>
      {/* Agentic Funnel */}
      <div data-id="element-1283">
        <h2 className="text-lg font-medium text-gray-900 mb-3" data-id="element-1284">
          Agentic Funnel
        </h2>
        <div className="grid grid-cols-5 gap-4" data-id="element-1285">
          <Card data-id="element-1286">
            <div className="pb-2 pt-4 px-4" data-id="element-1287">
              <div className="text-xs text-gray-500 uppercase" data-id="element-1288">
                Discovery Agent
              </div>
            </div>
            <CardContent className="pt-0" data-id="element-1289">
              <div className="flex items-baseline gap-2" data-id="element-1290">
                <div className="text-2xl font-bold" data-id="element-1291">
                  {(data.agents[0].processed / 1000000).toFixed(2)}M
                </div>
                <span className="text-xs text-green-600 font-medium" data-id="element-1292">↑</span>
              </div>
              <div className="text-xs text-gray-500" data-id="element-1293">Invoices Indexed</div>
            </CardContent>
          </Card>
          <Card data-id="element-1294">
            <div className="pb-2 pt-4 px-4" data-id="element-1295">
              <div className="text-xs text-gray-500 uppercase" data-id="element-1296">
                Extraction Agent
              </div>
            </div>
            <CardContent className="pt-0" data-id="element-1297">
              <div className="flex items-baseline gap-2" data-id="element-1298">
                <div className="text-2xl font-bold" data-id="element-1299">
                  {Math.round(data.agents[1].processed / 1000)}K
                </div>
                <span className="text-xs text-green-600 font-medium" data-id="element-1300">↑</span>
              </div>
              <div className="text-xs text-gray-500" data-id="element-1301">Fields Extracted</div>
            </CardContent>
          </Card>
          <Card data-id="element-1302">
            <div className="pb-2 pt-4 px-4" data-id="element-1303">
              <div className="text-xs text-gray-500 uppercase" data-id="element-1304">
                Validation Agent
              </div>
            </div>
            <CardContent className="pt-0" data-id="element-1305">
              <div className="flex items-baseline gap-2" data-id="element-1306">
                <div className="text-2xl font-bold" data-id="element-1307">
                  {Math.round(data.agents[2].processed / 1000)}K
                </div>
                <span className="text-xs text-green-600 font-medium" data-id="element-1308">↑</span>
              </div>
              <div className="text-xs text-gray-500" data-id="element-1309">Links Verified</div>
            </CardContent>
          </Card>
          <Card data-id="element-1310">
            <div className="pb-2 pt-4 px-4" data-id="element-1311">
              <div className="text-xs text-gray-500 uppercase" data-id="element-1312">
                Underwriting Agent
              </div>
            </div>
            <CardContent className="pt-0" data-id="element-1313">
              <div className="flex items-baseline gap-2" data-id="element-1314">
                <div className="text-2xl font-bold" data-id="element-1315">
                  {Math.round(data.agents[3].processed / 1000)}K
                </div>
                <span className="text-xs text-green-600 font-medium" data-id="element-1316">↑</span>
              </div>
              <div className="text-xs text-gray-500" data-id="element-1317">Receivables Scored</div>
            </CardContent>
          </Card>
          <Card data-id="element-1318">
            <div className="pb-2 pt-4 px-4" data-id="element-1319">
              <div className="text-xs text-gray-500 uppercase" data-id="element-1320">
                On-Chain Audit
              </div>
            </div>
            <CardContent className="pt-0" data-id="element-1321">
              <div className="flex items-baseline gap-2" data-id="element-1322">
                <div className="text-2xl font-bold" data-id="element-1323">
                  {(data.agents[4].processed / 1000).toFixed(1)}K
                </div>
                <span className="text-xs text-green-600 font-medium" data-id="element-1324">↑</span>
              </div>
              <div className="text-xs text-gray-500" data-id="element-1325">Blocks Verified</div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>;
}